<div class="modal-header">
  <div class="modal-title-wrapper">
    <h2 class="modal-title fw-bold">{{ package.package_name }}</h2>
    <div class="class-meta d-flex gap-4 mt-2">
      <span class="text-muted">
        <i class="fa fa-calendar-check me-2"></i>
        {{ package.session_count }} {{ package.session_count !== 1 ? ('manageEvent.viewPackageRegistrations.sessions' | translate) : ('manageEvent.viewPackageRegistrations.session' | translate) }}
      </span>
      <span class="text-muted">
        <i class="fa fa-money-bill me-2"></i>
        {{ formatPrice(package.price_cents, package.currency) }}
      </span>
      <span class="text-muted">
        <i class="fa fa-users me-2"></i>
        {{ registrations.length }} {{ registrations.length !== 1 ? ('manageEvent.viewPackageRegistrations.registrationsPlural' | translate) : ('manageEvent.viewPackageRegistrations.registrationSingular' | translate) }}
      </span>
    </div>
  </div>
  <button type="button" class="btn-close" (click)="close()"></button>
</div>

<div class="modal-body">
  @if (package.package_description) {
    <div class="alert alert-info d-flex align-items-center mb-6">
      <i class="fa fa-info-circle fs-2x me-3"></i>
      <div>{{ package.package_description }}</div>
    </div>
  }

  <!-- Add Registration Button -->
  <div class="d-flex justify-content-end mb-4">
    <app-button
      [variant]="'primary'"
      [label]="'manageEvent.viewPackageRegistrations.addRegistration' | translate"
      [iconClass]="'fa fa-plus'"
      (clicked)="openAddRegistrationModal()"
    />
  </div>

  @if (loading) {
    <div class="text-center py-10">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'common.loading' | translate }}</span>
      </div>
    </div>
  } @else if (registrations.length === 0) {
    <div class="text-center py-10">
      <i class="fa fa-users-slash fs-3x text-muted mb-5"></i>
      <h4 class="text-muted">{{ 'manageEvent.viewPackageRegistrations.noRegistrations' | translate }}</h4>
      <p class="text-muted">{{ 'manageEvent.viewPackageRegistrations.noRegistrationsHint' | translate }}</p>
    </div>
  } @else {
    <!-- Registrations Table -->
    <div class="table-responsive">
      <table class="table table-row-bordered table-row-dashed gy-4 align-middle fw-bold">
        <thead class="fw-bold text-muted bg-light">
          <tr>
            <th class="ps-6">#</th>
            <th>{{ 'manageEvent.viewPackageRegistrations.participant' | translate }}</th>
            <th>{{ 'manageEvent.viewPackageRegistrations.horse' | translate }}</th>
            <th>{{ 'manageEvent.viewPackageRegistrations.status' | translate }}</th>
            <th>{{ 'manageEvent.viewPackageRegistrations.payment' | translate }}</th>
            <th>{{ 'manageEvent.viewPackageRegistrations.sessionsColumn' | translate }}</th>
            <th>{{ 'manageEvent.viewPackageRegistrations.registeredOn' | translate }}</th>
            <th>{{ 'manageEvent.viewRegistrations.notes' | translate }}</th>
            <th class="text-end min-w-70px">{{ 'manageEvent.viewPackageRegistrations.actions' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (registration of registrations; track registration.registration_id; let i = $index) {
            <tr>
              <td class="ps-6 fw-semibold text-gray-700">{{ i + 1 }}</td>
              <td>
                @if (registration.user) {
                  <div class="d-flex flex-column">
                    <span class="fw-semibold text-gray-800">
                      {{ registration.user.first_name }} {{ registration.user.last_name }}
                    </span>
                    @if (registration.user.email) {
                      <span class="text-muted fs-7">{{ registration.user.email }}</span>
                    }
                  </div>
                } @else {
                  <span class="text-muted">N/A</span>
                }
              </td>
              <td>
                @if (registration.horse) {
                  <div class="d-flex flex-column">
                    <span class="fw-semibold text-gray-800">{{ registration.horse.name }}</span>
                    @if (registration.horse.breed) {
                      <span class="text-muted fs-7">{{ registration.horse.breed }}</span>
                    }
                  </div>
                } @else {
                  <span class="text-muted">{{ 'manageEvent.viewPackageRegistrations.notSpecified' | translate }}</span>
                }
              </td>
              <td>
                @if (registration.status === 'confirmed') {
                  <span class="badge badge-light-success">{{ 'manageEvent.viewPackageRegistrations.statusConfirmed' | translate }}</span>
                } @else if (registration.status === 'pending') {
                  <span class="badge badge-light-warning">{{ 'manageEvent.viewPackageRegistrations.statusPending' | translate }}</span>
                } @else if (registration.status === 'cancelled') {
                  <span class="badge badge-light-danger">{{ 'manageEvent.viewPackageRegistrations.statusCancelled' | translate }}</span>
                }
              </td>
              <td>
                @if (registration.payment_status === 'paid') {
                  <span class="badge badge-light-success">{{ 'manageEvent.viewPackageRegistrations.paymentPaid' | translate }}</span>
                } @else if (registration.payment_status === 'pending' || registration.payment_status === 'pay_later') {
                  <span class="badge badge-light-warning">{{ 'manageEvent.viewPackageRegistrations.paymentPending' | translate }}</span>
                } @else if (registration.payment_status === 'refunded') {
                  <span class="badge badge-light-danger">{{ 'manageEvent.viewPackageRegistrations.paymentRefunded' | translate }}</span>
                } @else {
                  <span class="badge badge-light-secondary">{{ 'manageEvent.viewPackageRegistrations.paymentNotRequired' | translate }}</span>
                }
              </td>
              <td>
                <span class="badge badge-light-primary">
                  {{ registration.sessions_scheduled }} / {{ package.session_count }}
                </span>
                @if (registration.sessions_remaining > 0) {
                  <span class="text-muted fs-7 ms-1">({{ registration.sessions_remaining }} {{ 'manageEvent.viewPackageRegistrations.left' | translate }})</span>
                }
              </td>
              <td>
                @if (registration.created_at) {
                  <span class="text-gray-700">{{ formatDateDMYDots(registration.created_at) }}</span>
                } @else {
                  <span class="text-muted">-</span>
                }
              </td>
              <td>
                @if (registration.notes) {
                  <div
                    class="notes-indicator d-inline-flex align-items-center gap-2 cursor-pointer"
                    [ngbTooltip]="notesTooltip"
                    tooltipClass="notes-tooltip"
                    placement="top"
                    container="body"
                  >
                    <i class="fa fa-comment-dots text-primary"></i>
                    <span class="notes-preview text-muted fs-7">{{ registration.notes.length > 20 ? (registration.notes.slice(0, 20) + '...') : registration.notes }}</span>
                  </div>
                  <ng-template #notesTooltip>
                    <div class="notes-tooltip-content">
                      <div class="notes-tooltip-header">
                        <i class="fa fa-comment-dots me-2"></i>
                        {{ 'manageEvent.viewRegistrations.participantNotes' | translate }}
                      </div>
                      <div class="notes-tooltip-body">{{ registration.notes }}</div>
                    </div>
                  </ng-template>
                } @else {
                  <span class="text-muted">-</span>
                }
              </td>
              <td class="text-end">
                <div class="dropdown">
                  <button
                    class="btn btn-sm btn-icon btn-light btn-active-light-primary"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i class="fa fa-ellipsis-v"></i>
                  </button>
                  <ul class="dropdown-menu dropdown-menu-end">
                    <li>
                      <a class="dropdown-item" (click)="openEditRegistrationModal(registration)">
                        <i class="far fa-edit me-2"></i>
                        {{ 'manageEvent.viewPackageRegistrations.editRegistration' | translate }}
                      </a>
                    </li>
                    <li><hr class="dropdown-divider"></li>
                    <li>
                      <a class="dropdown-item text-danger" (click)="deleteRegistration(registration)">
                        <i class="far fa-trash-can me-2"></i>
                        {{ 'manageEvent.viewPackageRegistrations.deleteRegistration' | translate }}
                      </a>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <!-- Summary Section -->
    <div class="separator my-6"></div>

    <div class="row g-5">
      <div class="col-md-4">
        <div class="card bg-light-primary">
          <div class="card-body p-5">
            <div class="d-flex align-items-center">
              <i class="fa fa-users fs-2x text-primary me-4"></i>
              <div>
                <div class="fs-7 text-muted">{{ 'manageEvent.viewPackageRegistrations.totalRegistrations' | translate }}</div>
                <div class="fs-2 fw-bold text-gray-800">{{ registrations.length }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card bg-light-success">
          <div class="card-body p-5">
            <div class="d-flex align-items-center">
              <i class="fa fa-check-circle fs-2x text-success me-4"></i>
              <div>
                <div class="fs-7 text-muted">{{ 'manageEvent.viewPackageRegistrations.paidPending' | translate }}</div>
                <div class="fs-2 fw-bold text-gray-800">{{ paidCount }} / {{ pendingCount }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card bg-light-warning">
          <div class="card-body p-5">
            <div class="d-flex align-items-center">
              <i class="fa fa-money-bill fs-2x text-warning me-4"></i>
              <div>
                <div class="fs-7 text-muted">{{ 'manageEvent.viewPackageRegistrations.revenuePaid' | translate }}</div>
                <div class="fs-2 fw-bold text-gray-800">{{ netRevenue | number:'1.0-0' }} {{ package.currency }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  }
</div>

<div class="modal-footer">
  <app-button
    [variant]="'outline'"
    [label]="'common.close' | translate"
    (clicked)="close()"
  />
</div>
